import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import BluebirdPromise from 'bluebird';
import { Redirect } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import download from 'downloadjs';
import { get, isEmpty, keyBy } from 'lodash';
import { ProgressBar } from 'react-bootstrap';
import Page from '../../../shared/components/Page';
import FullPageLoadingIndicator from '../../../shared/components/FullPageLoadingIndicator';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../shared/components/VerbButton';
import RecommendationModal from '../../../shared/components/modals/RecommendationModal';
import RecommendedCollectionsSection from './RecommendedCollectionsSection';
import { postPeerRecommendations } from '../../../shared/actions/PeerRecommendations';
import Banner from "@paycor/banner";
import PathwayTimelineModal from '../widgets/PathwayTimelineModal';
import { calculatePercentage } from '../../../shared/utils/NumberUtils';

import {
    getBadge,
    getManyCollections,
    getManyUserCollections,
    getManyUserPathways,
    getFurtherLearningCollections,
    getManyTeams,
    postCompletionNotification
} from '../../../shared/actions/index';
import { getFileWithSignedUrl } from '../../../shared/actions/Files';
import { makeGetObjectsFromStateByFunction } from '../../../shared/selectors/index';
import VerbHeading from '../../../shared/components/VerbHeading';
import VerbText from '../../../shared/components/VerbText';
import Image from '../../../shared/components/Image';
import { IMAGE_PROPS, createUserCertificate } from '../../../shared/utils/ImageUtils';

import { SEGMENT_EVENTS, LICENSE_TYPES } from '../../../shared/Constants';
import { RuntimeContext } from '../../../../common/RuntimeContext';
import copyIdToClipboard from '../../../shared/utils/AdminUtils';

export class CollectionInterstitial extends Component {
    static propTypes = {
        getBadge: PropTypes.func,
        userCollection: PropTypes.object,
        badge: PropTypes.object,
        assetsLoading: PropTypes.bool,
        collectionLoading: PropTypes.bool,
        teamsById: PropTypes.object,
        loggedInUser: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        loggedInUserCompany: PropTypes.object,
        collection: PropTypes.object,
        getManyCollections: PropTypes.func,
        viewableCollections: PropTypes.arrayOf(PropTypes.object),
        furtherLearningCollections: PropTypes.arrayOf(PropTypes.object),
        getManyUserCollections: PropTypes.func,
        postPeerRecommendations: PropTypes.func,
        getManyTeams: PropTypes.func,
        isAccountOwner: PropTypes.bool,
        associatedPathway: PropTypes.object,
        collectionsById: PropTypes.object,
        getManyUserPathways: PropTypes.func,
        getFurtherLearningCollections: PropTypes.func,
        userPathwaysById: PropTypes.object,
        postCompletionNotification: PropTypes.func
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            modalOpen: false,
            pathwayModalOpen: false,
            loading: true,
            recommendationsSent: false,
            emailManagerBannerMessage: null,
            emailManagerBannerType: '',
            showLinkCopiedBanner: false
        };
    }

    componentDidMount() {
        const {
            getBadge,
            getManyCollections,
            getManyUserCollections,
            getManyTeams,
            getManyUserPathways,
            loggedInUser,
            userPathwaysById,
            getFurtherLearningCollections,
            collection
        } = this.props;
        const promises = [
            getManyUserCollections({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb }),
            getManyCollections({ pathsContext: this.context.pathsWeb }),
            getManyTeams({ pathsContext: this.context.pathsWeb }),
            getFurtherLearningCollections({ userCollectionId: collection._id, pathsContext: this.context.pathsWeb })
        ];

        if (isEmpty(userPathwaysById)) {
            promises.push(
                getManyUserPathways({ userProfileId: loggedInUser._id, pathsContext: this.context.pathsWeb })
            );
        }

            BluebirdPromise.all(promises).spread((userCollection) => {
            const secondaryPromises = [];
            if (userCollection.badgeId) {
                secondaryPromises.push(getBadge({ id: userCollection.badgeId, pathsContext: this.context.pathsWeb }));
            }

            // TODO: Make this have a catch block and/or a finally block
            return BluebirdPromise.all(secondaryPromises).then(() => {
                this.setState((currentState, props) => {
                    if (!props.assetsLoading && !props.collectionLoading) {
                        return { loading: false };
                    }
                    return null;
                });
            });
        });
    }


    _makeUserCollectionThumbnail = (className) => {
        const {
            userCollection: { thumbnailImageUrl }
        } = this.props;

        const imageProps = IMAGE_PROPS.collectionImageCloudinary;
        
        return (
            <Image
                src={thumbnailImageUrl}
                dimension={imageProps.dimension}
                cloudinaryCrop={imageProps.cloudinaryCrop}
                className={`trackCardImage ${className || ''}`}
            />
        );
    };

    _handleResourceDownloadClick = (resourceUrl) => {
        const { loggedInUserCompany, isVerbAdmin, userCollection, collection } = this.props;
        getFileWithSignedUrl(this.context.pathsWeb, resourceUrl);
    };

    _renderProgressBar = () => {
        const { userCollection, collection } = this.props;
        const userCollLearningAssetsByLearningAssetId = keyBy(userCollection.content, 'learningAssetId');
        const totalCollectionAssets = collection.contentIds?.length ?? 0;
        const totalCollectionAssetsComplete = collection.contentIds.reduce(
            (acc, learningAsset) => {
                const userLearningAsset = userCollLearningAssetsByLearningAssetId[learningAsset.id];
                if (userLearningAsset.isCompleted) {
                    return acc + 1;
                }
                return acc;
            },
            0
        );
        const progress = calculatePercentage(totalCollectionAssetsComplete, totalCollectionAssets);
        return (
            <div className={'progress-bar-container'} hidden={!progress}>
                <VerbHeading
                    className={'progress-bar-label'}
                    messageId={'COLLECTION_INTERSTITIAL_PROGRESS_TEXT'}
                    hNumber={6}
                    defaultMessage={'PATH Complete'}
                />
                <ProgressBar now={progress} />
            </div>
        )
    }

    handleClick = (permaLink) => {
        this.props.history.push(`/collection/${permaLink}`);
    }

    _renderCongrats = () => {
        const { userCollection, badge, loggedInUser } = this.props;
        const { recommendationsSent, emailManagerBannerMessage, emailManagerBannerType, showLinkCopiedBanner } = this.state;
        const managerNotified = !!userCollection?.managerNotified;
        const noManagerAssigned = loggedInUser?.managerPresent === false;
        const noManagerBannerMessage = { messageId: 'EMAIL_MANAGER_NO_MANAGER_ERROR_BANNER', defaultMessage: "We're sorry, you don't have an assigned manager. Please contact your Company Administrator for help." };
        const effectiveBannerMessage = emailManagerBannerMessage
            || (managerNotified ? { messageId: 'EMAIL_MANAGER_SUCCESS_BANNER', defaultMessage: 'Your manager has been notified.' } : null)
            || (noManagerAssigned ? noManagerBannerMessage : null);
        const effectiveBannerType = emailManagerBannerMessage
            ? emailManagerBannerType
            : (managerNotified ? 'success' : (noManagerAssigned ? 'error' : ''));
        const isEmailManagerDisabled = managerNotified || emailManagerBannerType === 'success' || noManagerAssigned;
        const thumbnail = this._makeUserCollectionThumbnail('congrats-thumbnail');  

        const recomendationText = recommendationsSent ? (
            <div className={'col-12 recommend-container'}>
                <div className={'row'}>
                    <div className={'description-text recommend-collection-text col-12'}>
                        <VerbHeading
                            className={'recommendation-sent'}
                            messageId={'COLLECTION_INTERSTITIAL_RECOMMENDATIONS_SENT'}
                            defaultMessage={'Recommendations sent!'}
                            valuesToInject={{
                                CollectionName: userCollection.name || ''
                            }}
                        />
                    </div>
                    <br />
                    {/* <VerbButton
                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                        textId={'SEND_ANOTHER_RECOMMENDATION'}
                        defaultMessage={'Send Another Recommendation'}
                        onClick={() => this._handleRecommendation()}
                        className={'col-12 col-sm-8 col-md-6'}
                    /> */}
                </div>
            </div>
        ) : (
            <div className={'recommend-container col-12'}>
                <div className={'row'}>
                    <div className={'description-text recommend-collection-text col-12'}>
                        <VerbHeading
                            hNumber={4}
                            className={'col-sm-12'}
                            messageId={'COLLECTION_INTERSTITIAL_RECOMMEND_COLLECTION'}
                            defaultMessage={'Do you know someone who would also enjoy {CollectionName}?'}
                            valuesToInject={{
                                CollectionName: userCollection.name || ''
                            }}
                        />
                    </div>
                    <br />
                    {/* <VerbButton
                        textId={'RECOMMEND_THIS_COLLECTION'}
                        defaultMessage={'Recommend this collection'}
                        onClick={() => this._handleRecommendation()}
                        className={'recommend-button col-md-6 col-sm-8 col-12'}
                    /> */}
                </div>
            </div>
        );

        return (
            <div className={'container congrats-section-container'}>
                <div className={'congrats-section col-12 mx-0'}>
                    <div onClick={() => {this.handleClick(userCollection.collectionPermalink)}}>
                        {thumbnail}
                    </div>
                    <div className={'congrats-container'}>
                        <VerbHeading
                            className={'congrats-header'}
                            messageId={'NOTIFY_MANAGER_HEADING'}
                            defaultMessage={'Tell your manager!'}
                            hNumber={1}
                        />
                        <div className={'description-text congrats-description col-12'}>
                            <div className='congrats-description-1'>
                                <VerbText
                                    className={'col-sm-12 mx-0'}
                                    messageId={'COLLECTION_INTERSTITIAL_CONGRATS_TEXT'}
                                    defaultMessage={
                                        'Paths can email your manager that you completed the {CollectionName} Path.'
                                    }
                                    valuesToInject={{
                                        CollectionName: (
                                            <span className={'collection-name'}>{userCollection.name || ''}</span>
                                        )
                                    }}
                                />
                            </div>
                            <div className='congrats-description-2'>
                                <VerbText
                                    className={'col-sm-12 mx-0'}
                                    messageId={'COLLECTION_INTERSTITIAL_CONGRATS_TEXT_2'}
                                    defaultMessage={
                                        'Or copy the link to add it in your 1-on-1 or performance review.'
                                    }
                                />
                            </div>
                        </div>
                        {/* {recomendationText} */}
                    </div>
                </div>
                <div className='email-manager-container'>
                    <VerbButton
                        className={'col-5 collection-interstitial-email-manager'}
                        buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                        buttonText={'Email Manager'}
                        onClick={this._handleEmailManager}
                        disabled={isEmailManagerDisabled}
                    />
                    <VerbButton
                        buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                        className={'collection-interstitial-copy-btn'}
                        fontAwesomeIcon={'fa-link'}
                        onClick={() => {
                            copyIdToClipboard(`${window.location.origin}/collection/${userCollection.collectionPermalink}`);
                            this.setState({ showLinkCopiedBanner: true });
                        }}
                    />
                </div>
                <div style={!!effectiveBannerMessage ? { marginTop: '16px' } : undefined}>
                    {!!effectiveBannerMessage && (
                        <div className={`notify-banner-message-${effectiveBannerType}`}>
                            <i className={`fa ${effectiveBannerType === 'error' ? 'fa-info-circle' : 'fa-check'} icon`}/>
                            <div className=''>{effectiveBannerMessage?.defaultMessage}</div>
                            {effectiveBannerMessage?.messageId === 'EMAIL_MANAGER_GENERIC_ERROR_BANNER' && (
                                <i className={'fa icon fa-times'} onClick={() => this.setState({ emailManagerBannerMessage: null, emailManagerBannerType: '' })}/>
                            )}
                        </div>
                    )}
                </div>
                <div style={showLinkCopiedBanner ? { marginTop: '16px' } : undefined}>
                    {
                        showLinkCopiedBanner && <div className='notify-banner-message-success'>
                        <i className={'fa fa-check icon'}/>
                        <div className=''>Link copied.</div>
                        <i className={'fa icon fa-times'} onClick={() => this.setState({ showLinkCopiedBanner: false })}/>
                    </div>
                    }
                </div>
            </div>
        );
    };

    _handleEmailManager = () => {
        const { postCompletionNotification, loggedInUser, collection } = this.props;
        postCompletionNotification({
            userId: loggedInUser._id,
            collectionId: collection._id,
            pathsContext: this.context.pathsWeb
        }).then(() => {
            this.setState({
                emailManagerBannerMessage: { messageId: 'EMAIL_MANAGER_SUCCESS_BANNER', defaultMessage: 'Your manager has been notified.' },
                emailManagerBannerType: 'success'
            });
        }).catch((err) => {
            const isNoManager = err && err.message === 'No Manager found';
            const defaultMessage = isNoManager
                ? "We're sorry, you don't have an assigned manager. Please contact your Company Administrator for help."
                : "We're sorry, something didn't go as planned. Please refresh the page and try again.";
            const messageId = isNoManager ? 'EMAIL_MANAGER_NO_MANAGER_ERROR_BANNER' : 'EMAIL_MANAGER_GENERIC_ERROR_BANNER';
            this.setState({
                emailManagerBannerMessage: { messageId, defaultMessage },
                emailManagerBannerType: 'error'
            });
        });
    };

    _handleRecommendation = (hasRecomendationToSend = false) => {
        const { modalOpen } = this.state;

        this.setState({
            recommendationsSent: hasRecomendationToSend,
            modalOpen: !modalOpen
        });
    };

    _downloadCert = (certUrl, fileName) => {
        return fetch(certUrl)
            .then((res) => res.blob())
            .then((blob) => download(blob, fileName));
    };

    _renderDownloads = () => {
        const { userCollection, collection, loggedInUser } = this.props;

        const collectionResources = get(userCollection, 'resources', []) || [];
        const resourcesToRender = [...collectionResources];

        const userName = `${loggedInUser.firstName || ''} ${loggedInUser.lastName}`;
        const date = userCollection.completedOn || userCollection.updatedOn;
        const certificateUrl = createUserCertificate(userName, collection.name, date, 'COLLECTION');
        const certificate = { name: 'Certificate', url: certificateUrl, isCertificate: true };
        const certFileName = `${userCollection.collectionName.split(' ').join('_')}_Certificate`;
        resourcesToRender.unshift(certificate);

        if (!resourcesToRender || !resourcesToRender.length) {
            return null;
        }

        const resourceDownloads = resourcesToRender.map((resource, index) => (
            <div
                key={`${certFileName}-${index}`}
                className={'resource-download col-12 col-sm-6'}
                onClick={
                    resource.isCertificate
                        ? () => this._downloadCert(certificateUrl, certFileName)
                        : () => this._handleResourceDownloadClick(resource.url)
                }
            >
                <div className={'icon-container'}>
                    <span className={`fal ${resource.isExternalUrl ? 'fa-graduation-cap' : 'fa-file-alt'} col-sm-3`} />
                </div>
                <div className={'resource-name'}>
                    <strong>{resource.isCertificate ? 'Certificate' : resource.name}</strong>
                </div>
            </div>
        ));

        return (
            <div className={'col-12 collection-downloadables-container'}>
                <div className={'container'}>
                    <VerbHeading
                        className={'section-heading'}
                        hNumber={4}
                        messageId={'DOWNLOADS_HEADING'}
                        defaultMessage={'New downloads available!'}
                    />
                    <div className={'section-description description-text'}>
                        <VerbText
                            messageId={'DOWNLOADS_TEXT'}
                            defaultMessage={`Here are the downloadable items for ${userCollection.name}. Don't worry - if you can't download them right now, you can always find them on the ${userCollection.name} collection page.`}
                            valuesToInject={{
                                CollectionName: <span className={'collection-name'}>{userCollection.name || ''}</span>
                            }}
                        />
                    </div>
                    <div className={'downloads-section'}>{resourceDownloads}</div>
                </div>
            </div>
        );
    };

    handlePathwayModalClick = () =>
        this.setState((prevstate) => {
            const { pathwayModalOpen } = prevstate;
            return { pathwayModalOpen: !pathwayModalOpen };
        });

    render() {
        const {
            userCollection = {},
            collection = {},
            viewableCollections = [],
            loggedInUser,
            postPeerRecommendations,
            teamsById,
            loggedInUserCompany,
            isVerbAdmin,
            isAccountOwner,
            associatedPathway = {},
            collectionsById,
            furtherLearningCollections,
        } = this.props;
        const { loading, modalOpen, pathwayModalOpen } = this.state;
        if (loading) {
            return <FullPageLoadingIndicator />;
        }
        if (userCollection.status !== 'COMPLETED') {
            return <Redirect to={`/collection/${userCollection.collectionPermalink || userCollection.collectionId}`} />;
        }
        return (
            <Page className={'collection-interstitial-v2'}>
                <div className={'col-sm-12'}>
                    <div className={'collection-complete-container'}>
                        {this._renderProgressBar()}
                        {this._renderCongrats()}
                    </div>
                    <RecommendedCollectionsSection
                        collectionsById={collectionsById}
                        associatedPathway={associatedPathway}
                        collection={collection}
                        company={loggedInUserCompany}
                        userCollection={userCollection}
                        collectionOptions={furtherLearningCollections}
                        loggedInUserCompany={loggedInUserCompany}
                        handleClickPathway={this.handlePathwayModalClick}
                    />
                    {collection.type !== 'MINI' && this._renderDownloads()}
                </div>
                <RecommendationModal
                    isOpen={modalOpen}
                    onClose={this._handleRecommendation}
                    parentId={userCollection.collectionId}
                    onSend={postPeerRecommendations}
                    companyId={loggedInUser.companyId}
                    company={loggedInUserCompany}
                    loggedInUser={loggedInUser}
                    currentUserEmail={loggedInUser.email}
                    parentType={'COLLECTION'}
                    teamsById={teamsById}
                    parentEntity={collection}
                    isVerbAdmin={isVerbAdmin}
                    isAccountOwner={isAccountOwner}
                />
                <PathwayTimelineModal
                    isOpen={pathwayModalOpen}
                    handleModalClose={this.handlePathwayModalClick}
                    userPathwayId={associatedPathway._id}
                />
            </Page>
        );
    }
}

export const makeMapStateToProps = () => {
    const getCollectionsFromStateById = makeGetObjectsFromStateByFunction('collections.byId');
    const mapStateToProps = (state, ownProps) => {
        const {
            collections,
            userCollections,
            auth: { loggedInUser, loggedInUserCompanyId, isVerbAdmin, isAccountOwner },
            userLearningAssets: { loading },
            badges,
            companies,
            userPathways = {},
            teams
        } = state;
        const {
            match: {
                params: { userCollectionId }
            }
        } = ownProps;
        const loggedInUserCompany = (companies.byId && companies.byId[loggedInUserCompanyId]) || {};
        const companyCollectionLicenseLookup = (loggedInUserCompany.collectionLicenses || []).reduce((acc, curr) => {
            acc[curr.collectionId] = curr;
            return acc;
        }, {});
        const userCollectionsByCollectionId = Object.keys(userCollections.byId).reduce((acc, key) => {
            if (userCollections.byId[key].collectionId) {
                acc[userCollections.byId[key].collectionId] = userCollections.byId[key];
            }
            return acc;
        }, {});
        const filterFunction = (collection) => {
            if (
                !collection.isActive ||
                (collection._id in userCollectionsByCollectionId &&
                    userCollectionsByCollectionId[collection._id].status === 'COMPLETED')
            ) {
                return false;
            }
            if (
                !collection.subscriptionType ||
                (collection.subscriptionType || '').toLocaleUpperCase() === LICENSE_TYPES.AVAILABLE_FOR_ALL_UPPER
            ) {
                return true;
            }
            if (
                (collection.subscriptionType || '').toLocaleUpperCase() ===
                LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE_UPPER
            ) {
                return collection._id in companyCollectionLicenseLookup;
            }
            return false;
        };

        const viewableCollections = getCollectionsFromStateById(state, filterFunction);
        const userCollection = userCollections.byId[userCollectionId];
        const collection = collections.byId[userCollection && userCollection.collectionId];

        let associatedPathway;
        const allUserPathways = Object.values(userPathways.byId);

        for (let i = 0; i < allUserPathways.length; i += 1) {
            for (let j = 0; j < (allUserPathways[i].content || []).length; j += 1) {
                const pathwayContentItem = allUserPathways[i].content[j];
                if (pathwayContentItem.userSubscriptionId === userCollectionId) {
                    associatedPathway = allUserPathways[i];
                    break;
                }
            }
        }

        let badge;
        if (userCollection && userCollection.badgeId) {
            badge = badges.byId[userCollection.badgeId];
        }
        
        const { furtherLearningCollections = [] } = state.collections;
    
        return {
            collection,
            collectionsById: collections.byId,
            userCollection,
            collectionLoading: userCollections.loading,
            assetsLoading: loading,
            badge,
            loggedInUser,
            loggedInUserCompany,
            isVerbAdmin,
            isAccountOwner,
            viewableCollections,
            teamsById: teams.byId,
            associatedPathway,
            userPathwaysById: userPathways.byId,
            furtherLearningCollections
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    getBadge: (params) => dispatch(getBadge(params)),
    getManyCollections: (params) => dispatch(getManyCollections(params)),
    getManyUserCollections: (params) => dispatch(getManyUserCollections(params)),
    postPeerRecommendations: (params) => dispatch(postPeerRecommendations(params)),
    getManyTeams: (params) => dispatch(getManyTeams(params)),
    getManyUserPathways: (params) => dispatch(getManyUserPathways(params)),
    getFurtherLearningCollections: (params) => dispatch(getFurtherLearningCollections(params)),
    postCompletionNotification: (params) => dispatch(postCompletionNotification(params))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(injectIntl(CollectionInterstitial));
